import { useState, useEffect } from 'react';
import { API, formatWeight, formatDateTime, todayIST, displayDate } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function ReconciliationPage() {
  const [ledger, setLedger] = useState([]);
  const [selectedRecon, setSelectedRecon] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reconDate, setReconDate] = useState(todayIST());
  const [prevDate, setPrevDate] = useState('');
  const [reconType, setReconType] = useState('morning_to_night');

  const fetchLedger = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await API.get('/reconciliation/ledger', { params });
      setLedger(res.data);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { fetchLedger(); }, [startDate, endDate]);

  const handleRunRecon = async () => {
    try {
      let res;
      if (reconType === 'morning_to_night') {
        res = await API.post(`/reconciliation/morning-to-night?date=${reconDate}`);
      } else {
        const url = prevDate ? `/reconciliation/night-to-morning?date=${reconDate}&previous_date=${prevDate}` : `/reconciliation/night-to-morning?date=${reconDate}`;
        res = await API.post(url);
      }
      if (res.data.all_match) toast.success('ALL MATCHED!');
      else toast.error('MISMATCHES found!');
      fetchLedger();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewDetail = async (recon) => {
    try { const res = await API.get(`/reconciliation/${recon.id}`); setSelectedRecon(res.data); }
    catch(e) { setSelectedRecon(recon); }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Reconciliation</h1>
        <p className="hidden sm:block">Day-wise stock reconciliation records</p>
      </div>

      <Card className="mb-4 md:mb-6">
        <CardHeader className="pb-2"><CardTitle className="text-base">Run Reconciliation</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Type</label>
              <div className="flex rounded-lg border overflow-hidden">
                <button className={`px-2 sm:px-3 py-2 text-xs sm:text-sm whitespace-nowrap ${reconType === 'morning_to_night' ? 'bg-primary text-primary-foreground' : 'bg-white'}`}
                  onClick={() => setReconType('morning_to_night')}>AM to PM</button>
                <button className={`px-2 sm:px-3 py-2 text-xs sm:text-sm whitespace-nowrap ${reconType === 'night_to_morning' ? 'bg-primary text-primary-foreground' : 'bg-white'}`}
                  onClick={() => setReconType('night_to_morning')}>PM to AM</button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Date</label>
              <Input type="date" value={reconDate} onChange={e => setReconDate(e.target.value)} className="w-full sm:w-40" />
            </div>
            {reconType === 'night_to_morning' && (
              <div>
                <label className="text-xs font-medium mb-1 block">Previous Date</label>
                <Input type="date" value={prevDate} onChange={e => setPrevDate(e.target.value)} className="w-full sm:w-40" />
              </div>
            )}
            <Button onClick={handleRunRecon} className="w-full sm:w-auto">Run</Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
        <div><label className="text-xs text-muted-foreground">From</label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full sm:w-40" /></div>
        <div><label className="text-xs text-muted-foreground">To</label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full sm:w-40" /></div>
      </div>

      {/* Mobile: Card list */}
      <div className="mobile-card-list">
        {ledger.map(r => (
          <Card key={r.id} className="cursor-pointer" onClick={() => viewDetail(r)}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono font-semibold text-sm">{displayDate(r.date)}</span>
                {r.all_match ? <span className="status-badge status-matched text-xs"><CheckCircle size={12} /> OK</span>
                  : <span className="status-badge status-mismatch text-xs"><XCircle size={12} /> Mismatch</span>}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline" className="text-xs">{(r.recon_type || '').replace(/_/g, ' ')}</Badge>
                {r.night_skipped && <Badge variant="secondary" className="text-xs">Night skipped</Badge>}
              </div>
            </CardContent>
          </Card>
        ))}
        {ledger.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No records yet</p>}
      </div>

      {/* Desktop: Table */}
      <div className="desktop-table">
        <Card>
          <CardContent className="p-0">
            <div className="table-scroll-wrapper">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Night Skipped</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledger.map(r => (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => viewDetail(r)}>
                      <TableCell className="font-mono font-semibold">{displayDate(r.date)}</TableCell>
                      <TableCell><Badge variant="outline">{(r.recon_type || '').replace(/_/g, ' ')}</Badge></TableCell>
                      <TableCell>
                        {r.all_match ? <span className="status-badge status-matched"><CheckCircle size={12} /> Matched</span>
                          : r.status === 'approved' ? <span className="status-badge status-pending"><AlertTriangle size={12} /> Approved</span>
                          : <span className="status-badge status-mismatch"><XCircle size={12} /> Mismatch</span>}
                      </TableCell>
                      <TableCell>{r.night_skipped ? <Badge variant="secondary">Yes</Badge> : '—'}</TableCell>
                      <TableCell className="text-sm">{formatDateTime(r.created_at)}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); viewDetail(r); }}>Details</Button></TableCell>
                    </TableRow>
                  ))}
                  {ledger.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No records</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedRecon} onOpenChange={() => setSelectedRecon(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-base">Recon: {displayDate(selectedRecon?.date)}</DialogTitle></DialogHeader>
          {selectedRecon && (
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant={selectedRecon.all_match ? 'default' : 'destructive'}>{selectedRecon.all_match ? 'All Matched' : 'Mismatches'}</Badge>
                {selectedRecon.night_skipped && <Badge variant="secondary">Night Skipped</Badge>}
              </div>
              <div className="table-scroll-wrapper">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Box</TableHead>
                      <TableHead className="text-right">Morning</TableHead>
                      <TableHead className="text-right">Night/Exp</TableHead>
                      <TableHead className="text-right">Diff</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedRecon.box_results || []).map((r, i) => (
                      <TableRow key={i} className={!r.matches ? 'bg-rose-50' : ''}>
                        <TableCell className="font-mono font-semibold text-xs">{r.box_id}</TableCell>
                        <TableCell className="text-right weight-value text-xs">{r.morning_gross_g != null ? `${r.morning_gross_g}g` : '—'}</TableCell>
                        <TableCell className="text-right weight-value text-xs">{r.night_gross_g != null ? `${r.night_gross_g}g` : r.expected_night_g != null ? `${r.expected_night_g}g` : '—'}</TableCell>
                        <TableCell className="text-right weight-value text-xs">{r.difference_g != null ? `${r.difference_g}g` : '—'}</TableCell>
                        <TableCell>{r.matches ? <span className="status-badge status-matched text-xs"><CheckCircle size={10} /> OK</span> : <span className="status-badge status-mismatch text-xs"><XCircle size={10} /> {r.status}</span>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
